import { type google } from '@google-cloud/documentai/build/protos/protos';
import { type OcrResult } from '@prisma/client';
import { type ForwardRefExoticComponent, type RefAttributes, type SVGProps } from 'react';
import { type Context } from '~/server/api/trpc';
import { type PageRouteCtx } from '~/server/middlewares/withUserAccessToken';
import { type DenseCaptionResponse } from '~/server/utils/models/azureVision';

export type JsonLocation =
  | {
      pageNumber: number;
      position?: number;
    }
  | undefined;

export interface AsanaResource {
  gid: string;
  name: string;
  resource_type: string;
}

export type JiraAccessibleResources = {
  id: string;
  name: string;
  url: string;
  scopes: string[];
  avatarUrl: string;
};

export interface AsanaAttachment {
  gid: string;
  name: string;
  parent: {
    created_by?: {
      name: string;
    };
  };
  size: number;
  permanent_url: string;
  download_url: string;
}

export type RolesApiResponse = Record<string, string>;

export interface AssignableUserReponse {
  self: string;
  accountId: string;
  accountType: string;
  displayName: string;
  emailAddress: string;
  timeZone: string;
}

/**
 * JIRA statuses are known as transitions when we want to change it on a issue
 */
export interface JIRATransitions {
  expand: string;
  transitions: {
    id: string;
    name: string;
  }[];
}

export interface OAuthFlowUrlInput {
  access_type?: string;
  audience: string;
  baseUrl: string;
  clientId: string;
  code_challenge?: string;
  code_challenge_method?: string;
  redirectUri: string;
  scopes: string[];
  state: string;
}

export type Tokens = {
  access_token: string;
  refresh_token: string;
  scope: string;
  token_type: string;
  /** In seconds */
  expires_in: number | null;
  /** Unix Epoch in seconds */
  expiry_date: number | null;
};

export interface JQLResponse {
  expand: string;
  issues: JiraIssue[];
  names: Record<string, string>;
  maxResults: number;
  startAt: number;
  total: number;
}

export interface JiraIssue {
  renderedFields: {
    description: string;
  };
  expand: string;
  fields: {
    project: {
      key: string;
    };
    attachment: JiraAttachment[];
    description: {
      type: string;
      version: number;
      content: [];
    };
    assignee: {
      displayName: string;
      emailAddress: string;
    };
    summary: string;
    creator: {
      displayName: string;
    };
    status: {
      name: string;
    };
    created: string;
    duedate?: string | null;
  };
  names: Record<string, string>;
  id: string;
  key: string;
  self: string;
}

export interface JiraStatuses {
  self: string;
  id: string;
  name: string;
  statuses: {
    id: string;
    name: string;
  }[];
}

export type JiraProjectInfo = {
  expand: string;
  self: string;
  id: string;
  key: string;
  description: string;
  lead: {
    self: string;
    accountId: string;
    avatarUrls: {
      '48x48': string;
      '24x24': string;
      '16x16': string;
      '32x32': string;
    };
    displayName: string;
    active: boolean;
  };
  components: any[]; // You can replace "any" with a specific type if you have one
  issueTypes: {
    self: string;
    id: string;
    description: string;
    iconUrl: string;
    name: string;
    subtask: boolean;
    avatarId: number;
    hierarchyLevel: number;
  }[];
  assigneeType: string;
  versions: any[]; // You can replace "any" with a specific type if you have one
  name: string;
  roles: {
    [key: string]: string;
  };
  avatarUrls: {
    '48x48': string;
    '24x24': string;
    '16x16': string;
    '32x32': string;
  };
  projectTypeKey: string;
  simplified: boolean;
  style: string;
  isPrivate: boolean;
  properties: any; // You can replace "any" with a specific type if you have one
  entityId: string;
  uuid: string;
};

export interface JiraAttachment {
  author: {
    displayName: string;
  };
  content: string;
  created: string;
  filename: string;
  id: number;
  mimeType: string;
  size: number;
}

export interface PageToReview {
  text: string;
  imageUrl?: string;
  pageNumber: number;
  startChar: number;
  endChar: number;
}

export interface SmartsheetSheet {
  id: number;
  name: string;
  sourceSheets: {
    id: number;
    name: string;
    permalink: string;
    columns: SmartsheetColumn[];
    title: string;
    options?: string[];
  }[];
  totalRowCount: number;
  accessLevel: string;
  permalink: string;
  createdAt: string;
  modifiedAt: string;
  cardSettings?: {
    viewByColumnId: number;
    displayColumnIds: number[];
  };
  workspace?: {
    id: number;
    name: string;
  };
  columns: SmartsheetColumn[];
  rows: SmartsheetRow[];
}

export interface SmartsheetPaginationResponse<T> {
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  data: T[];
}

export interface SmartsheetColumn {
  virtualId: number;
  id: number;
  title: string;
  description?: string;
  type: 'PICKLIST' | 'TEXT_NUMBER' | 'CHECKBOX' | 'DATE';
  options?: string[];
}

export interface SmartsheetRow {
  id: number;
  sheetId: number;
  rowNumber: number;
  expanded: boolean;
  createdAt: string;
  createdBy: {
    name: string;
    email: string;
  };
  modifiedAt: string;
  cells: {
    virtualColumnId: number;
    columnId: number;
    value: string;
    displayValue: string;
  }[];
  attachments: SmartsheetAttachment[];
}

export interface SmartsheetAttachment {
  id: number;
  name: string;
  attachmentType: string;
  mimeType: string;
  sizeInKb: number;
  createdAt: Date;
  createdBy: {
    name: string;
    email: string;
  };
  url?: string;
  urlExpiresInMillis?: 0;
}

export interface SmartsheetUser {
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  id: number;
}

export const protegeColumns = ['Task ID', 'Name', 'Description', 'Status'] as const;

export type ProtegeColumn = (typeof protegeColumns)[number];

export type ReportOptions = Record<
  string,
  {
    isSelected: boolean;
    type: 'checkbox' | 'radio';
  }
>;
export interface WrikeResponse<T> {
  kind: string;
  nextPageToken?: string;
  responseSize?: string;
  data: T;
}

export interface WrikeFolderListData {
  id: string;
  title: string;
  childIds: [];
  scope: string;
  project?: {
    authorId: string;
    ownerIds: string[];
    customStatusId: string;
    createdDate: string;
  };
}

export interface WrikeFolder {
  id: string;
  accountId: string;
  title: string;
  createdDate: string;
  updatedDate: string;
  description: string;
  sharedIds: string[];
  parentIds: string[];
  childIds: [];
  superParentIds: [];
  scope: string;
  hasAttachments: false;
  permalink: string;
  workflowId: string;
  metadata: [];
  customFields: { id: string; value: string }[];
  project: {
    authorId: string;
    ownerIds: string[];
    customStatusId: string;
    createdDate: string;
  };
}

export interface WrikeTask {
  id: string;
  accountId: string;
  title: string;
  status: string;
  importance: string;
  createdDate: string;
  updatedDate: string;
  responsibleIds: string[];
  dates: {
    type: string;
    due?: string | null;
  };
  scope: string;
  customStatusId: string;
  permalink: string;
  priority: string;
  hasAttachments: boolean;
  authorIds: string[];
  description: string;
}

export interface WrikeWorkflow {
  id: string;
  name: string;
  standard: boolean;
  hidden: boolean;
  customStatuses: { id: string; name: string }[];
}

export interface WrikeUser {
  id: string;
  firstName: string;
  lastName: string;
  type: string;
  primaryEmail: string;
}

export interface WrikeWorkspace {
  id: string;
  title: string;
  avatarUrl: string;
  accessType: string;
  archived: boolean;
  guestRoleId: string;
  defaultProjectWorkflowId: string;
  defaultTaskWorkflowId: string;
}

export interface WrikeAttachment {
  id: string;
  authorId: string;
  name: string;
  createdDate: string;
  contentType: string;
  size: number;
  taskId: string;
  originVersionId: string;
}

export interface EscalatedTask {
  taskId: string;
  status?: string;
  reviewer?: string;
  comment?: string;
}

export const fileAccessProvidersList = [
  'FRAMEIO',
  'GOOGLE_APIS',
  'SHAREPOINT',
  'AIRTABLE',
  'FIGMA',
] as const;
export type FileAccessProviders = (typeof fileAccessProvidersList)[number];

export type Activity = {
  createdAt: string;
  text: string;
  id: string;
  createdBy: string;
  isAuto?: boolean;
  /** True when this row is a comment (including comments shown under All Activities). */
  isComment?: boolean;
};

export interface AllActivities {
  activities: Activity[];
  comments: Activity[];
}

export interface JiraChangelog {
  self: string;
  maxResults: number;
  startAt: number;
  total: number;
  isLast: boolean;
  values: {
    id: string;
    author: {
      displayName: string;
    };
    created: string;
    items: {
      field: string;
      fromString: string;
      fieldtype: string;
      fieldId: string;
      toString: string;
    }[];
  }[];
}

export interface JiraComments {
  startAt: number;
  maxResults: number;
  total: number;
  comments: {
    id: string;
    author: { displayName: string };
    body: { content: { type: string; content: { text: string }[] }[] };
    created: string;
    renderedBody: string;
  }[];
}

export interface OcrParagraph extends google.cloud.documentai.v1.Document.Page.IParagraph {
  /** Translated text for the paragraph if the detected language is not English */
  translatedText?: string;
}

export interface OcrPage extends google.cloud.documentai.v1.Document.IPage {
  paragraphs?: OcrParagraph[] | null;
}

export interface OcrResultWithTranslations extends google.cloud.documentai.v1.IDocument {
  /** Translated text for the whole image if the detected language is not English */
  translatedText?: string;
  pages?: OcrPage[] | null;
  text?: string | null;
}

export interface IOcrResult extends Omit<OcrResult, 'rawData' | 'rawDenseCaption'> {
  rawData: OcrResultWithTranslations | null;
  rawDenseCaption: DenseCaptionResponse | null;
}

export type ApiReqCtx = Context | PageRouteCtx;

export type THeroIcon = ForwardRefExoticComponent<
  Omit<SVGProps<SVGSVGElement>, 'ref'> & {
    title?: string;
    titleId?: string;
  } & RefAttributes<SVGSVGElement>
>;
